import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import { Observable, Subject, Subscription, delay, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { Clock } from '../../models/clock.model';
import { Socket } from 'ngx-socket-io';
import { SocketService } from '../../../_core/services/socket.service';
import { ClockName } from '../../models/enums/clock-name.enum';
import { ClockType } from '../../models/enums/clock-type.enum';
import { Match } from '../../models/match.model';
import { ClocksService } from '../../../clocks/clocks.service';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { GestureController } from '@ionic/angular';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { AuthService } from '../../../auth/auth.service';
import { User } from '../../models/user.model';

export enum StopwatchDisplayTypeEnum {
    OFFICIAL_VIEW = "OFFICIAL_VIEW",
    TIMED_TENNIS_SCOREBOARD = "TIMED_TENNIS_SCOREBOARD",
    PLAIN = "PLAIN",
    INTENNSE_VIEW = 'INTENNSE_VIEW'
}

@Component({
  standalone: false,
    selector: 'app-stopwatch',
    templateUrl: './stopwatch.component.html',
    styleUrls: ['./stopwatch.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class StopwatchComponent implements OnChanges, AfterViewInit {
    private socket = inject(Socket);
    private socketService = inject(SocketService);
    private clocksService = inject(ClocksService);
    private activatedRoute = inject(ActivatedRoute);
    private datePipe = inject(DatePipe);
    private gestureController = inject(GestureController);

    @ViewChild('gestureArea', { static: false }) gestureArea?: any;
    @ViewChild('timePicker', { static: true }) timePicker!: TimePickerComponent;

    @Input({ required: true }) clockInputValue: Clock;
    @Input({ required: true }) displayType: StopwatchDisplayTypeEnum;
    @Input({ required: false }) match: Match;
    @Input({ required: false }) showButtons = true;
    @Input({ required: false }) rowHeight;
    @Output() boltClockEnd = new EventEmitter<void>();
    @Output() pausePlay = new EventEmitter<'pause' | 'play'>();
    public authService = inject(AuthService);

    clock = signal({
        _id: undefined,
        name: ClockName.DEFAULT,
        type: ClockType.STOPWATCH,
        defaultValue: 1,
        value: 1,
        isPaused: true,
        timePipe: 'm:ss',
        adjustTime: 1000
    });

    timer$: Observable<number> = timer(0, 1000).pipe(takeUntilDestroyed());
    clockSubscription: Subscription;

    isPickerOpen = false;
    private destroyed$: Subject<void> = new Subject();
    broadcastDelay: number = 0;
    private longPressTimeout: any;
    tapCount = 0;
    tapTimeout: number | null;
    allowToSendUpdateClock:boolean = false;

    isGestureCreated = false;
    isClockUpdateNotify = false;
    constructor() {
        this.broadcastDelay = Number(this.activatedRoute.snapshot.queryParams.broadcastDelay) || 0;
    }

    async ngOnInit() {
        const authUser: User = await this.authService.getAuthUser();
        const scoreSubscribers = this.match.scoreSubscribers || [];
        this.allowToSendUpdateClock = this.canSendUpdateClock(authUser, scoreSubscribers);
    }

    ngAfterViewInit(): void {
        if (this.gestureArea && !this.isGestureCreated) {
            const gesture = this.gestureController.create({
                el: this.gestureArea.nativeElement,
                gestureName: 'stopwatch-gesture',
                threshold: 0, // Ensures the gesture starts immediately
                gesturePriority: 10, // Prioritize this gesture over defaults
                onStart: () => this.handleTouchStart(),
                onEnd: (ev) => this.handleTouchEnd(),
            });

            gesture.enable(true);

            this.isGestureCreated = true;
        }
    }

    async ngOnChanges(changes: SimpleChanges) {
        const clockInputValue = changes.clockInputValue?.currentValue
        if (clockInputValue) {
            if (!clockInputValue.value) clockInputValue.value = clockInputValue.defaultValue;
            this.clock.set(clockInputValue);
            if (!this.clock()?.isPaused) this.startClock();

            if (this.displayType !== StopwatchDisplayTypeEnum.OFFICIAL_VIEW && this.displayType !== StopwatchDisplayTypeEnum.INTENNSE_VIEW) {
                this.socketService.joinRoom(`clock/${this.clock()._id}`);
                this.socketService.getSocketUpdate('clock')
                    .pipe(
                        delay(this.broadcastDelay * 1000)
                    )
                    .subscribe((data: { clock: Clock }) => {
                        if (data?.clock && data.clock._id === this.clock()._id) {
                            this.clock.set(data.clock);
                            if (!this.clock()?.isPaused) this.startClock();
                        }
                    });
            }
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        const clockId = this.clock()._id;        
        if (clockId) {
            this.clocksService.removeClock(clockId);
        }
    }

    startPauseClock() {
        this.isClockUpdateNotify = false;
        if (this.clock().isPaused) {
            this.startClock();
            this.pausePlay.emit('play');
        } else {
            this.pauseClock();
            this.pausePlay.emit('pause');
        }
    }

    startClock() {
        this.clock.update(clock => ({
            ...clock, ...{
                isPaused: false
            },
        }))
        this.updateClockOverSocket();
        if (this.match?.scoreSubscribers?.includes('ross') || this.allowToSendUpdateClock) {  
            this.updateClocksForPartnerOverSocket();         
        }        

        this.clockSubscription?.unsubscribe();
        this.clockSubscription = this.timer$.pipe(
            delay(1000),
            tap(() => {
                // if (this.match?.scoreSubscribers?.includes('ross') || this.allowToSendUpdateClock) {  
                //     this.updateClocksForPartnerOverSocket();                  
                //     this.updateClockOverSocket(true);
                // }
                // Check if time has ended
                if (this.clock().value <= 0 && this.displayType === 'INTENNSE_VIEW') {
                    this.pauseClock();
                }
            }),
            takeWhile(() => this.clock().value > 0 && !this.clock().isPaused),
        ).subscribe(() => {
            this.clock.update(clock => ({
                ...clock, ...{
                    value: clock.value + clock.adjustTime
                },
                displayValue: this.datePipe.transform(clock.value + clock.adjustTime, clock.timePipe)
            }))
            if (this.match?.scoreSubscribers?.includes('ross') || this.allowToSendUpdateClock) {  
                this.updateClocksForPartnerOverSocket();                  
                this.updateClockOverSocket(true);
            }

            if(this.clock().value === 0 && this.clock().name === ClockName.SET_TIME){
                this.boltClockEnd.emit();
            }
        })
    }

    pauseClock() {
        this.clock.update(clock => ({
            ...clock, ...{
                displayValue: this.datePipe.transform(clock.value, clock.timePipe),
                isPaused: true
            }
        }))
        this.updateClockOverSocket();
    }

    clearClock(isPaused = true) {
        this.clock.update(clock => ({
            ...clock, ...{
                value: clock.defaultValue,
                displayValue: this.datePipe.transform(clock.defaultValue, clock.timePipe),
                isPaused
            }
        }))
        this.updateClockOverSocket();
    }

    updateClock(value: number) {
        this.clock.update(clock => ({
            ...clock, ...{
                value,
                displayValue: this.datePipe.transform(value, clock.timePipe),
                isPaused: true
            }
        }))

        this.updateClockOverSocket();
    }

    updateClockOverSocket(skipSocketEmit?: boolean) {
        if ((this.displayType === StopwatchDisplayTypeEnum.OFFICIAL_VIEW || this.displayType === StopwatchDisplayTypeEnum.INTENNSE_VIEW) && this.clock()._id) {
            this.clocksService.update(this.match._id, this.clock(), {isStartPoint: false, isEndPoint: false})
                .pipe(takeUntil(this.destroyed$)
                ).subscribe();
            if(skipSocketEmit) return;
            this.socket.emit('clock-update', { clock: this.clock() });
        }
    }

    updateClocksForPartnerOverSocket() {
        const currentClock: any = this.clock();

        //Save current clock in service
        this.clocksService.storeClock(currentClock);

        //Filter out current clock from match.clocks
        const filteredClocks = (this.match.clocks || [])
        .filter((clock: any) => clock._id !== currentClock._id)
        .map((clock: any) => {
            const latest = this.clocksService.getLatestClock(clock._id);
            return latest ?? clock;
        });

        //Add currentClock to the list
        const updatedClocks = [...filteredClocks, currentClock];

        //  Emit payload
        const clockPayload = {
            id: this.match._id,
            matchId: this.match.matchId,
            matchType: this.match.matchType,
            scoreSubscribers: this.match.scoreSubscribers,
            clocks: updatedClocks,
            eventFlags: { isClockUpdate: true },
            isUndo: false
        };

        this.socket.emit('partner-clock-update', { match: clockPayload });
    }

    handleTouchStart() {
        this.longPressTimeout = setTimeout(() => {
            this.timePicker.setOpen(true);
        }, 500);
    }

    handleTouchEnd() {
        clearTimeout(this.longPressTimeout);
        this.tapCount++; // Increment tap count for single/double click

        // Wait to determine if it's a double tap
        if (!this.tapTimeout) {
            this.tapTimeout = window.setTimeout(() => {
                if (this.tapCount === 1) {
                    this.startPauseClock();
                } else if (this.tapCount === 2) {
                    this.clearClock();
                }

                // Reset tap count and timeout
                this.tapCount = 0;
                this.tapTimeout = null;
            }, 300); // Adjust the double-tap threshold as needed
        }
    }

    deductTime(milliseconds: number) {
        if (this.clockInputValue) {
            this.clockInputValue.value -= milliseconds;
            // Update the clock signal with new value
            this.clock.update(clock => ({
                ...clock,
                value: clock.value - milliseconds,
                displayValue: this.datePipe.transform(
                    clock.value - milliseconds,
                    clock.timePipe
                )
            }));
            this.updateClockOverSocket();
        }
    }

    canSendUpdateClock(authUser: User, scoreSubscribers: string[]): boolean {
        if (!authUser || !Array.isArray(scoreSubscribers) || scoreSubscribers.length === 0) return false;

        const { role, allowMatchSubscription, partner } = authUser;

        const isAdmin = role?.value === 500;
        const isPublisherAllowed = !!allowMatchSubscription;        
        const isSubscribedPartner = Array.isArray(partner)? partner.some(p => scoreSubscribers.includes(p)): typeof partner === 'string' && scoreSubscribers.includes(partner);

        return isAdmin || (isPublisherAllowed && !!isSubscribedPartner);
    }

    public getCurrentClock(): any {
      return this.clock(); // or whatever property holds the current time
    }

}
